document.addEventListener('DOMContentLoaded', () => {
  /** @type {NodeListOf<HTMLElement>} */
  const sections = document.querySelectorAll('[data-at-ebook]');
  if (!sections.length) return;

  const isWideAspect = () => window.matchMedia('(min-aspect-ratio: 6/5)').matches;

  // ========================================
  // ページ管理（電子書籍機能の基準）
  // ========================================
  /** @type {Map<HTMLElement, { currentPage: number, totalPages: number }>} */
  const pageStates = new Map();

  /** @type {Map<HTMLElement, ZoomManager>} */
  const zoomManagers = new Map();
  /** @type {Map<HTMLElement, Map<number, Promise<void>>>} */
  const slideLoadPromises = new Map();

  /** @param {HTMLElement} section */
  const getPageState = (section) => {
    let state = pageStates.get(section);
    if (!state) {
      const slides = section.querySelectorAll('.AT_ebook-slide');
      state = { currentPage: 0, totalPages: slides.length };
      pageStates.set(section, state);
    }
    return state;
  };

  /** @param {HTMLElement} section */
  const isLoopEnabled = (section) => {
    const raw = (section.getAttribute('data-at-ebook-loop') || '').trim().toLowerCase();
    return raw === 'true' || raw === '1' || raw === 'yes' || raw === 'on';
  };

  /**
   * @param {HTMLElement} section
   * @param {number} index
   */
  const normalizePageIndex = (section, index) => {
    const state = getPageState(section);
    if (state.totalPages <= 0) return 0;
    if (isLoopEnabled(section)) {
      return ((index % state.totalPages) + state.totalPages) % state.totalPages;
    }
    return Math.max(0, Math.min(index, state.totalPages - 1));
  };

  /** @param {HTMLElement} section */
  const getSlidePromiseMap = (section) => {
    let map = slideLoadPromises.get(section);
    if (!map) {
      map = new Map();
      slideLoadPromises.set(section, map);
    }
    return map;
  };

  /** @param {HTMLImageElement} img */
  const isImageReady = (img) => img.complete && img.naturalWidth > 0;

  /**
   * @param {HTMLElement} section
   * @param {number} index
   * @param {{ showLoading: boolean }} options
   */
  const ensureSlideLoaded = (section, index, options = { showLoading: false }) => {
    const normalizedIndex = normalizePageIndex(section, index);
    const slides = getSlides(section);
    const slide = slides[normalizedIndex];
    if (!slide) return Promise.resolve();

    const slideEl = /** @type {HTMLElement} */ (slide);
    const images = /** @type {HTMLImageElement[]} */ (Array.from(slideEl.querySelectorAll('img')));
    if (!images.length) return Promise.resolve();

    const pendingImages = images.filter((img) => !isImageReady(img));
    if (!pendingImages.length) {
      slideEl.classList.remove('AT_ebook-slide--loading');
      return Promise.resolve();
    }

    const promises = getSlidePromiseMap(section);
    const existing = promises.get(normalizedIndex);
    if (existing) {
      if (options.showLoading) slideEl.classList.add('AT_ebook-slide--loading');
      return existing.finally(() => {
        slideEl.classList.remove('AT_ebook-slide--loading');
      });
    }

    if (options.showLoading) slideEl.classList.add('AT_ebook-slide--loading');

    const loadPromise = Promise.all(
      pendingImages.map((img) => {
        img.loading = 'eager';
        if (typeof img.decode === 'function') {
          return img
            .decode()
            .catch(() => undefined)
            .then(() => undefined);
        }
        return new Promise((resolve) => {
          const onDone = () => {
            resolve(undefined);
          };
          img.addEventListener('load', onDone, { once: true });
          img.addEventListener('error', onDone, { once: true });
        });
      })
    )
      .then(() => undefined)
      .finally(() => {
      promises.delete(normalizedIndex);
      slideEl.classList.remove('AT_ebook-slide--loading');
      });

    promises.set(normalizedIndex, loadPromise);
    return loadPromise;
  };

  /** @param {HTMLElement} section @param {number} centerIndex */
  const preloadNearbySlides = (section, centerIndex) => {
    ensureSlideLoaded(section, centerIndex, { showLoading: true });
    ensureSlideLoaded(section, centerIndex + 1, { showLoading: false });
    ensureSlideLoaded(section, centerIndex + 2, { showLoading: false });
    ensureSlideLoaded(section, centerIndex - 1, { showLoading: false });
  };

  /** @param {HTMLElement} section @param {number} index */
  const goToPage = (section, index) => {
    const state = getPageState(section);
    if (state.totalPages <= 0) return;
    const newPage = normalizePageIndex(section, index);

    if (newPage === state.currentPage) return;

    // ズームをリセット
    const zoomManager = zoomManagers.get(section);
    if (zoomManager) {
      zoomManager.resetZoom();
    }

    // ページを更新
    state.currentPage = newPage;
    section.setAttribute('data-current', String(newPage));

    // スライドの表示位置を更新
    updateSlidePosition(section, newPage);

    // UI更新
    updateSection(section, newPage);
    updateArrows(section);
    preloadNearbySlides(section, newPage);
  };

  /** @param {HTMLElement} section @param {number} index */
  const updateSlidePosition = (section, index) => {
    const slidesContainer = /** @type {HTMLElement | null} */ (section.querySelector('.AT_ebook-slides'));
    const slides = section.querySelectorAll('.AT_ebook-slide');
    if (!slidesContainer || !slides[index]) return;

    // 対象スライドの実際の位置を取得してスクロール
    const targetSlide = /** @type {HTMLElement} */ (slides[index]);
    const offset = targetSlide.offsetLeft;
    slidesContainer.style.transform = `translateX(-${offset}px)`;
  };

  /** @param {HTMLElement} section */
  const nextPage = (section) => {
    const state = getPageState(section);
    goToPage(section, state.currentPage + 1);
  };

  /** @param {HTMLElement} section */
  const prevPage = (section) => {
    const state = getPageState(section);
    goToPage(section, state.currentPage - 1);
  };

  /** @param {HTMLElement} section @returns {number} */
  const getCurrentPage = (section) => {
    return getPageState(section).currentPage;
  };

  // ========================================
  // ズーム設定（おまけ機能）
  // ========================================
  const ZOOM_CONFIG = {
    minScale: 1,
    maxScale: 4,
    scaleStep: 0.5,
  };

  /**
   * ズーム管理クラス（おまけ機能）
   */
  class ZoomManager {
    /** @param {HTMLElement} section */
    constructor(section) {
      this.section = section;
      this.scale = 1;
      this.translateX = 0;
      this.translateY = 0;
      this.currentZoomable = null;

      // ピンチ用
      this.initialDistance = 0;
      this.initialScale = 1;
      this.initialPinchCenter = { x: 0, y: 0 };

      // パン用
      this.isPanning = false;
      this.startX = 0;
      this.startY = 0;
      this.lastTranslateX = 0;
      this.lastTranslateY = 0;

      this.init();
    }

    init() {
      this.bindZoomButtons();
      this.bindTouchEvents();
      this.bindMouseEvents();
    }

    /** @returns {HTMLElement | null} */
    getCurrentSlide() {
      const currentPage = getCurrentPage(this.section);
      const slides = this.section.querySelectorAll('.AT_ebook-slide');
      return /** @type {HTMLElement | null} */ (slides[currentPage] || null);
    }

    /** @returns {HTMLElement | null} */
    getZoomableElement() {
      const slide = this.getCurrentSlide();
      if (!slide) return null;
      return /** @type {HTMLElement | null} */ (slide.querySelector('.AT_ebook-page__zoomable'));
    }

    /** @param {HTMLElement} zoomable */
    ensureZoomable(zoomable) {
      if (this.currentZoomable !== zoomable) {
        if (this.currentZoomable) {
          this.currentZoomable.style.transform = '';
          this.currentZoomable.classList.remove('AT_ebook-page__zoomable--dragging');
        }
        this.translateX = 0;
        this.translateY = 0;
        this.currentZoomable = zoomable;
      }
    }

    /**
     * @param {number} newScale
     * @param {number} [originX]
     * @param {number} [originY]
     */
    setScale(newScale, originX, originY) {
      const zoomable = this.getZoomableElement();
      if (!zoomable) return;

      this.ensureZoomable(zoomable);

      const oldScale = this.scale;
      this.scale = Math.min(Math.max(newScale, ZOOM_CONFIG.minScale), ZOOM_CONFIG.maxScale);

      if (originX !== undefined && originY !== undefined && oldScale !== this.scale) {
        const rect = zoomable.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const offsetX = originX - centerX;
        const offsetY = originY - centerY;

        const scaleDiff = this.scale / oldScale;
        this.translateX = offsetX - (offsetX - this.translateX) * scaleDiff;
        this.translateY = offsetY - (offsetY - this.translateY) * scaleDiff;
      }

      this.constrainTranslate(zoomable);
      this.applyTransform(zoomable);
      this.updateZoomState();
      this.updateZoomButtons();
    }

    /** @param {HTMLElement} zoomable */
    constrainTranslate(zoomable) {
      if (this.scale <= 1) {
        this.translateX = 0;
        this.translateY = 0;
        return;
      }

      const rect = zoomable.getBoundingClientRect();
      const scaledWidth = rect.width * this.scale;
      const scaledHeight = rect.height * this.scale;

      const maxTranslateX = Math.max(0, (scaledWidth - rect.width) / 2);
      const maxTranslateY = Math.max(0, (scaledHeight - rect.height) / 2);

      this.translateX = Math.min(Math.max(this.translateX, -maxTranslateX), maxTranslateX);
      this.translateY = Math.min(Math.max(this.translateY, -maxTranslateY), maxTranslateY);
    }

    /** @param {HTMLElement} zoomable */
    applyTransform(zoomable) {
      zoomable.style.transform = `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`;
    }

    updateZoomState() {
      const isZoomed = this.scale > 1;
      this.section.classList.toggle('AT_ebook--zoomed', isZoomed);
    }

    updateZoomButtons() {
      const zoomInBtn = this.section.querySelector('[data-at-ebook-zoom-in]');
      const zoomOutBtn = this.section.querySelector('[data-at-ebook-zoom-out]');
      const resetBtn = this.section.querySelector('[data-at-ebook-zoom-reset]');

      if (zoomInBtn instanceof HTMLButtonElement) {
        zoomInBtn.disabled = this.scale >= ZOOM_CONFIG.maxScale;
      }
      if (zoomOutBtn instanceof HTMLButtonElement) {
        zoomOutBtn.disabled = this.scale <= ZOOM_CONFIG.minScale;
      }
      if (resetBtn instanceof HTMLButtonElement) {
        resetBtn.disabled = this.scale === 1;
      }
    }

    resetZoom() {
      this.scale = 1;
      this.translateX = 0;
      this.translateY = 0;

      if (this.currentZoomable) {
        this.currentZoomable.style.transform = '';
        this.currentZoomable.classList.remove('AT_ebook-page__zoomable--dragging');
      }

      this.updateZoomState();
      this.updateZoomButtons();
      this.currentZoomable = null;
    }

    zoomIn() {
      this.setScale(this.scale + ZOOM_CONFIG.scaleStep);
    }

    zoomOut() {
      this.setScale(this.scale - ZOOM_CONFIG.scaleStep);
    }

    bindZoomButtons() {
      const zoomInBtn = this.section.querySelector('[data-at-ebook-zoom-in]');
      const zoomOutBtn = this.section.querySelector('[data-at-ebook-zoom-out]');
      const resetBtn = this.section.querySelector('[data-at-ebook-zoom-reset]');

      zoomInBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.zoomIn();
      });

      zoomOutBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.zoomOut();
      });

      resetBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.resetZoom();
      });

      this.updateZoomButtons();
    }

    bindTouchEvents() {
      const container = this.section.querySelector('.AT_ebook-slides-container');
      if (!container) return;

      /** @type {Touch[]} */
      let activeTouches = [];

      container.addEventListener('touchstart', (e) => {
        if (!this.section.classList.contains('AT_ebook--fullscreen')) return;

        activeTouches = Array.from(/** @type {TouchEvent} */ (e).touches);

        if (activeTouches.length === 2) {
          e.preventDefault();
          this.initialDistance = this.getDistance(activeTouches[0], activeTouches[1]);
          this.initialScale = this.scale;
          this.initialPinchCenter = this.getCenter(activeTouches[0], activeTouches[1]);
        } else if (activeTouches.length === 1 && this.scale > 1) {
          e.preventDefault();
          this.startPan(activeTouches[0].clientX, activeTouches[0].clientY);
        }
      }, { passive: false });

      container.addEventListener('touchmove', (e) => {
        if (!this.section.classList.contains('AT_ebook--fullscreen')) return;

        activeTouches = Array.from(/** @type {TouchEvent} */ (e).touches);

        if (activeTouches.length === 2) {
          e.preventDefault();
          const newDistance = this.getDistance(activeTouches[0], activeTouches[1]);
          const scaleRatio = newDistance / this.initialDistance;
          const newScale = this.initialScale * scaleRatio;
          const center = this.getCenter(activeTouches[0], activeTouches[1]);
          this.setScale(newScale, center.x, center.y);
        } else if (activeTouches.length === 1 && this.isPanning) {
          e.preventDefault();
          this.doPan(activeTouches[0].clientX, activeTouches[0].clientY);
        }
      }, { passive: false });

      container.addEventListener('touchend', (e) => {
        activeTouches = Array.from(/** @type {TouchEvent} */ (e).touches);
        if (activeTouches.length < 2) {
          this.initialDistance = 0;
        }
        if (activeTouches.length === 0) {
          this.endPan();
        }
      });
    }

    bindMouseEvents() {
      const container = this.section.querySelector('.AT_ebook-slides-container');
      if (!container) return;

      container.addEventListener('mousedown', (e) => {
        if (!this.section.classList.contains('AT_ebook--fullscreen')) return;
        if (this.scale <= 1) return;
        if (!(e instanceof MouseEvent)) return;
        if (e.button !== 0) return;

        e.preventDefault();
        e.stopPropagation();
        this.startPan(e.clientX, e.clientY);
      }, { capture: true });

      container.addEventListener('mousemove', (e) => {
        if (!this.isPanning) return;
        if (!(e instanceof MouseEvent)) return;
        e.preventDefault();
        e.stopPropagation();
        this.doPan(e.clientX, e.clientY);
      }, { capture: true });

      container.addEventListener('mouseup', (e) => {
        if (this.isPanning) {
          e.stopPropagation();
        }
        this.endPan();
      }, { capture: true });

      container.addEventListener('mouseleave', () => {
        this.endPan();
      });
    }

    /**
     * @param {number} x
     * @param {number} y
     */
    startPan(x, y) {
      this.isPanning = true;
      this.startX = x;
      this.startY = y;
      this.lastTranslateX = this.translateX;
      this.lastTranslateY = this.translateY;

      const zoomable = this.getZoomableElement();
      if (zoomable) {
        zoomable.classList.add('AT_ebook-page__zoomable--dragging');
      }
    }

    /**
     * @param {number} x
     * @param {number} y
     */
    doPan(x, y) {
      if (!this.isPanning) return;

      const zoomable = this.getZoomableElement();
      if (!zoomable) return;

      this.translateX = this.lastTranslateX + (x - this.startX);
      this.translateY = this.lastTranslateY + (y - this.startY);

      this.constrainTranslate(zoomable);
      this.applyTransform(zoomable);
    }

    endPan() {
      this.isPanning = false;

      const zoomable = this.getZoomableElement();
      if (zoomable) {
        zoomable.classList.remove('AT_ebook-page__zoomable--dragging');
      }
    }

    /**
     * @param {Touch} t1
     * @param {Touch} t2
     */
    getDistance(t1, t2) {
      const dx = t1.clientX - t2.clientX;
      const dy = t1.clientY - t2.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    /**
     * @param {Touch} t1
     * @param {Touch} t2
     */
    getCenter(t1, t2) {
      return {
        x: (t1.clientX + t2.clientX) / 2,
        y: (t1.clientY + t2.clientY) / 2,
      };
    }
  }

  // ========================================
  // ヘルパー関数
  // ========================================

  /** @param {HTMLElement} section */
  const getSlides = (section) => /** @type {HTMLElement[]} */ (Array.from(section.querySelectorAll('.AT_ebook-slide')));

  /** @param {HTMLElement | null | undefined} slide */
  const isSpreadSlide = (slide) => Boolean(slide && slide.classList.contains('AT_ebook-slide--spread'));

  /** @param {HTMLElement[]} slides */
  const getTotalPages = (slides) => {
    if (!isWideAspect()) return slides.length;
    return slides.reduce((sum, slide) => sum + (isSpreadSlide(slide) ? 2 : 1), 0);
  };

  /**
   * @param {HTMLElement[]} slides
   * @param {number} index
   */
  const getPageRange = (slides, index) => {
    if (!isWideAspect()) {
      const page = index + 1;
      return { start: page, end: page };
    }

    let start = 1;
    for (let i = 0; i < index; i += 1) {
      start += isSpreadSlide(slides[i]) ? 2 : 1;
    }

    const currentSpread = isSpreadSlide(slides[index]);
    const nextSpread = isSpreadSlide(slides[index + 1]);
    const end = currentSpread ? start + 1 : nextSpread ? start : start + 1;

    return { start, end };
  };

  /** @param {HTMLElement} section @param {number} index */
  const updateSection = (section, index = 0) => {
    const slides = getSlides(section);
    const total = getTotalPages(slides);
    const currentEl = section.querySelector('.AT_ebook-pagination__current');
    const totalEl = section.querySelector('.AT_ebook-pagination__total');
    const range = getPageRange(slides, index);
    if (currentEl) {
      currentEl.textContent = range.end > range.start ? `${range.start}-${range.end}` : String(range.start);
    }
    if (totalEl && total) totalEl.textContent = String(total);
    section.setAttribute('data-current', String(index));
  };

  /** @param {HTMLElement} section */
  const updateArrows = (section) => {
    const slides = getSlides(section);
    const totalSlides = slides.length;
    const currentValue = section.getAttribute('data-current');
    const current = currentValue != null ? parseInt(currentValue, 10) : 0;
    const loopEnabled = isLoopEnabled(section);
    const prevButton = section.querySelector('[data-at-ebook-prev]');
    const nextButton = section.querySelector('[data-at-ebook-next]');

    if (prevButton instanceof HTMLButtonElement) {
      if (totalSlides <= 1) {
        prevButton.disabled = true;
      } else {
        prevButton.disabled = loopEnabled ? false : current <= 0;
      }
    }
    if (nextButton instanceof HTMLButtonElement) {
      if (totalSlides <= 1) {
        nextButton.disabled = true;
      } else {
        nextButton.disabled = loopEnabled ? false : current >= Math.max(totalSlides - 1, 0);
      }
    }
  };

  /** @param {HTMLElement} section @param {boolean} enabled */
  const setFullscreen = (section, enabled) => {
    section.classList.toggle('AT_ebook--fullscreen', enabled);
    document.documentElement.classList.toggle('AT_ebook-no-scroll', enabled);
    document.body.classList.toggle('AT_ebook-no-scroll', enabled);
    updateArrows(section);

    if (!enabled) {
      const zoomManager = zoomManagers.get(section);
      if (zoomManager) {
        zoomManager.resetZoom();
      }
    }
  };

  // ========================================
  // 初期化
  // ========================================

  sections.forEach((section) => {
    // 初期状態を設定
    updateSection(section, 0);
    updateSlidePosition(section, 0);
    preloadNearbySlides(section, 0);

    const initial = section.getAttribute('data-at-ebook-initial');
    if (initial === 'true') {
      setFullscreen(section, true);
    }
    updateArrows(section);

    // ズームマネージャーを初期化
    const zoomManager = new ZoomManager(section);
    zoomManagers.set(section, zoomManager);

    // 閉じるボタン
    const closeButton = section.querySelector('[data-at-ebook-close]');
    if (closeButton) {
      closeButton.addEventListener('click', (event) => {
        event.preventDefault();
        setFullscreen(section, false);
      });
    }

    // 矢印ボタン
    const prevButton = section.querySelector('[data-at-ebook-prev]');
    if (prevButton) {
      prevButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        prevPage(section);
      });
    }

    const nextButton = section.querySelector('[data-at-ebook-next]');
    if (nextButton) {
      nextButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        nextPage(section);
      });
    }

    // スワイプ機能
    const container = section.querySelector('.AT_ebook-slides-container');
    if (container) {
      let swipeStartX = 0;
      let swipeStartY = 0;
      let isSwiping = false;

      container.addEventListener('touchstart', (e) => {
        const zoomManager = zoomManagers.get(section);
        // ズーム中はスワイプ無効（パンを優先）
        if (zoomManager && zoomManager.scale > 1) return;
        // 2本指はピンチなのでスワイプ無効
        if (e.touches.length !== 1) return;

        swipeStartX = e.touches[0].clientX;
        swipeStartY = e.touches[0].clientY;
        isSwiping = true;
      }, { passive: true });

      container.addEventListener('touchmove', (e) => {
        if (!isSwiping) return;
        const zoomManager = zoomManagers.get(section);
        if (zoomManager && zoomManager.scale > 1) {
          isSwiping = false;
          return;
        }
      }, { passive: true });

      container.addEventListener('touchend', (e) => {
        if (!isSwiping) return;
        isSwiping = false;

        const zoomManager = zoomManagers.get(section);
        if (zoomManager && zoomManager.scale > 1) return;

        const touch = e.changedTouches[0];
        if (!touch) return;

        const deltaX = touch.clientX - swipeStartX;
        const deltaY = touch.clientY - swipeStartY;
        const absDeltaX = Math.abs(deltaX);
        const absDeltaY = Math.abs(deltaY);

        // 横方向のスワイプかつ閾値を超えた場合
        const swipeThreshold = 50;
        if (absDeltaX > swipeThreshold && absDeltaX > absDeltaY) {
          if (deltaX < 0) {
            // 左スワイプ → 次のページ
            nextPage(section);
          } else {
            // 右スワイプ → 前のページ
            prevPage(section);
          }
        }
      }, { passive: true });
    }

    // 画像クリックで全画面モード
    section.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-at-ebook-close]')) return;
      if (target.closest('[data-at-ebook-zoom-in]')) return;
      if (target.closest('[data-at-ebook-zoom-out]')) return;
      if (target.closest('[data-at-ebook-zoom-reset]')) return;
      if (target.closest('[data-at-ebook-prev]')) return;
      if (target.closest('[data-at-ebook-next]')) return;

      // フルスクリーン中はリンク遷移を許可
      if (section.classList.contains('AT_ebook--fullscreen')) {
        return;
      }

      // 通常表示時：クリックで全画面モードに
      const page = target.closest('.AT_ebook-page');
      if (!page) return;
      const link = target.closest('a');
      if (link) event.preventDefault();
      setFullscreen(section, true);
    });
  });

  // リサイズ対応
  window.addEventListener('resize', () => {
    sections.forEach((section) => {
      const current = getCurrentPage(section);
      updateSection(section, current);
      updateSlidePosition(section, current);
      updateArrows(section);
      preloadNearbySlides(section, current);
    });
  });
});
